// Banner components for notifications and messages.

import { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import type { FormatParams, TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';
import { toHoverColor, toPressColor } from 'util/util-color';

import { HorizBox, HoverView, LoadingScreenStyle } from './basics';
import {
    colorSurfaceBannerInfo,
    colorSurfaceBannerWarning,
    colorSurfaceHighlight,
    colorTagHighlight,
    colorTagVerified,
    colorTextOnTagLight,
    colorTextPrimary,
} from './color';
import { UtilityText } from './text';

export type TopBannerProps = { rightIcon?: React.ReactNode; children: React.ReactNode };

export function TopBanner({ rightIcon, children }: TopBannerProps) {
    const s = TopBannerStyle;
    return (
        <View style={s.topBanner}>
            <View style={s.body}>{children}</View>
            {rightIcon && <View style={s.rightIcon}>{rightIcon}</View>}
        </View>
    );
}
const TopBannerStyle = StyleSheet.create({
    topBanner: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: colorSurfaceHighlight,
        paddingHorizontal: 20,
        paddingVertical: 15,
    },
    body: {
        flex: 1,
    },
    rightIcon: {
        marginLeft: 10,
    },
});

export type BannerProps = { color?: string; children: React.ReactNode };

export function Banner({ color = colorSurfaceBannerWarning, children }: BannerProps) {
    const s = BannerStyle;
    return <View style={[s.banner, { backgroundColor: color }]}>{children}</View>;
}
const BannerStyle = StyleSheet.create({
    banner: {
        paddingHorizontal: 20,
        paddingVertical: 15,
        borderRadius: 8,
        overflow: 'hidden',
    },
});

export type BannerMessageProps = { label?: TLabel; text?: string; formatParams?: FormatParams; error?: boolean };

export function BannerMessage({ label, text, formatParams, error }: BannerMessageProps) {
    return (
        <Banner color={error ? colorTagHighlight : colorSurfaceBannerInfo}>
            <UtilityText
                color={error ? colorTextOnTagLight : colorTextPrimary}
                formatParams={formatParams}
                label={label}
                text={text}
                weight='medium'
            />
        </Banner>
    );
}

export type ClickableBannerProps = {
    testID?: string;
    onPress?: (() => void) | null;
    color?: string;
    hoverColor?: string;
    pressColor?: string;
    children: React.ReactNode;
};

export function ClickableBanner({
    testID,
    onPress,
    color = colorSurfaceBannerWarning,
    hoverColor = toHoverColor(colorSurfaceBannerWarning),
    pressColor = toPressColor(colorSurfaceBannerWarning),
    children,
}: ClickableBannerProps) {
    const s = ClickableBannerStyle;
    return (
        <HoverView
            hoverStyle={{ backgroundColor: hoverColor }}
            onPress={onPress}
            pressedStyle={{ backgroundColor: pressColor }}
            style={[s.banner, { backgroundColor: color }]}
            testID={testID}
        >
            <HorizBox center spread>
                {children}
            </HorizBox>
        </HoverView>
    );
}
const ClickableBannerStyle = StyleSheet.create({
    banner: {
        paddingHorizontal: 20,
        paddingVertical: 15,
        borderRadius: 8,
    },
});

export type LoadingScreenProps = { label?: TLabel };

export function LoadingScreen({ label = t('Loading...') }: LoadingScreenProps) {
    const [show, setShow] = useState(false);
    const s = LoadingScreenStyle;

    useEffect(() => {
        const timeout = setTimeout(() => setShow(true), 1000);
        return () => clearTimeout(timeout);
    }, []);

    if (show) {
        return (
            <View style={s.outer}>
                {/* <UtilityText style={s.text} label={label} /> */}
                <Banner color={colorTagVerified}>
                    <UtilityText label={label} />
                </Banner>
            </View>
        );
    } else {
        return null;
    }
}
